import { Band, QueueDecisionState } from '../models';

// The queue presents a single "View" selector backed by two underlying filter
// axes (band + decisionState). Most reviewer mental models map cleanly to one
// of four named views; the orthogonal product has too many empty combos
// (Auto-approve + Decided, Auto-block + Decided, etc.) to be worth surfacing.
//
// The underlying filter shape stays unchanged so the backend contract isn't
// affected — we just compose the two axes here.

export type QueueView = 'pendingReview' | 'decided' | 'autoApproved' | 'autoBlocked';

export interface QueueViewFilters {
  band: Band;
  decisionState: QueueDecisionState;
}

const VIEW_TO_FILTERS: Record<QueueView, QueueViewFilters> = {
  pendingReview: { band: 'humanReview', decisionState: 'undecided' },
  decided: { band: 'humanReview', decisionState: 'decided' },
  autoApproved: { band: 'autoApprove', decisionState: null },
  autoBlocked: { band: 'autoBlock', decisionState: null },
};

export const QUEUE_VIEWS: readonly QueueView[] = [
  'pendingReview',
  'decided',
  'autoApproved',
  'autoBlocked',
];

export function filtersForQueueView(view: QueueView): QueueViewFilters {
  return VIEW_TO_FILTERS[view];
}

// Derive which named view (if any) corresponds to the current band+decisionState
// pair. Returns null for non-standard combos (e.g. a deep-linked URL with
// autoApprove + decided) so the UI can fall back to a default selection.
export function queueViewFromFilters(
  band: Band,
  decisionState: QueueDecisionState,
): QueueView | null {
  for (const view of QUEUE_VIEWS) {
    const target = VIEW_TO_FILTERS[view];
    if (target.band === band && target.decisionState === decisionState) {
      return view;
    }
  }
  return null;
}
